import { memo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useHesapAuth } from '../../store/hesapStore';
import { isHesapSubdomain } from '../../lib/subdomain';
import { toast } from 'sonner';
import useIdleLogout from '../../hooks/useIdleLogout';
import { Timer } from 'lucide-react';

/**
 * IdleSessionWidget — kendi state'i ile her saniye yenilenir,
 * dışarıdaki bileşenleri etkilemez. Sayaç sıfırlanınca otomatik logout yapar.
 */
const IdleSessionWidget = memo(function IdleSessionWidget() {
  const { token, logout } = useHesapAuth();
  const navigate = useNavigate();

  const handleLogout = useCallback(() => {
    logout();
    navigate(isHesapSubdomain() ? '/giris' : '/hesap/giris');
  }, [logout, navigate]);

  const onIdleTimeout = useCallback(() => {
    toast.error('1 saattir işlem yapılmadı — oturum kapatıldı', { duration: 4000 });
    handleLogout();
  }, [handleLogout]);

  const onIdleWarn = useCallback((sec) => {
    toast.warning(`Oturum ${Math.ceil(sec / 60)} dakika içinde kapatılacak. Devam etmek için herhangi bir yere tıklayın.`, { duration: 6000 });
  }, []);

  const { secondsLeft } = useIdleLogout({
    enabled: !!token,
    onTimeout: onIdleTimeout,
    onWarn: onIdleWarn,
  });

  const min = Math.floor((secondsLeft || 0) / 60);
  const sec = (secondsLeft || 0) % 60;
  const str = `${String(min).padStart(2, '0')}:${String(sec).padStart(2, '0')}`;
  const color = secondsLeft <= 120 ? 'text-red-600 bg-red-50 border-red-200 dark:bg-red-900/30 dark:border-red-700 dark:text-red-300'
              : secondsLeft <= 600 ? 'text-amber-600 bg-amber-50 border-amber-200 dark:bg-amber-900/30 dark:border-amber-700 dark:text-amber-300'
              : 'text-slate-600 bg-slate-50 border-slate-200 dark:bg-slate-800 dark:border-slate-700 dark:text-slate-300';

  return (
    <div className={`flex flex-col items-center justify-center text-[10px] px-2.5 py-1 border rounded-lg leading-tight flex-shrink-0 ${color}`}
      title="Oturum bitiş süresi" data-testid="idle-countdown">
      <div className="flex items-center gap-1 opacity-70">
        <Timer className="h-2.5 w-2.5" />
        <span className="hidden sm:inline">Oturum kapanmasına</span>
        <span className="sm:hidden">Oturum</span>
      </div>
      <span className="font-mono font-bold text-sm tracking-wider mt-0.5">{str}</span>
    </div>
  );
});

export default IdleSessionWidget;
